
import { Vue, Options, prop } from "vue-class-component";
import prefixAlgoEstimationShow from "@/app/infrastructures/misc/common-library/PrefixAlgoEstimationShow";
import { AccountController } from "@/app/ui/controllers/AccountController";
import { FlagsMedusa } from "@/feature-flags/flags-misc-medusa";
import { isShipmentC1Cod } from "@/app/ui/modules/receipt-v2/module";
import formatPrice from "@/app/infrastructures/misc/common-library/FormatPrice";
import { getCurrencyByCountryCode } from "@/app/infrastructures/misc/common-library/modules/v2/Price";
import { ShipmentBookingDetail } from "@/domain/entities/ShipmentBooking";
import startCase from "lodash/startCase";
import formatDateWithoutTime from "@/app/infrastructures/misc/common-library/FormatDateWithoutTime";
import { IS_SENDER_ACCOUNT } from "@/app/infrastructures/misc/Constants";
import { formatByCountry } from '@/app/infrastructures/misc/Utils';

class Props {
  detailData = prop<ShipmentBookingDetail>({
    default: new ShipmentBookingDetail(),
    type: ShipmentBookingDetail
  });
  isWoodpacking = prop<boolean>({
    default: false,
    type: Boolean
  });
  typeAccount = prop<string>({
    default: "",
    type: String
  });
  formAccountCurrencyPrefix = prop<string>({
    default: "Rp",
    type: String
  });
}

export default class DetailPaymentSection extends Vue.with(Props) {
  get isEnableCodBooking() {
    return FlagsMedusa.config_new_cod_booking_enable.isEnabled();
  }

  get accountCurrency(): "IDR" | "MYR" {
    return (
      (AccountController.accountData?.accountCurrency as "IDR" | "MYR") ?? "IDR"
    );
  }

  formatCurrency(value: any, bookedByCountry?: string) {
    if (IS_SENDER_ACCOUNT) {
      return formatPrice(Number(value), "", "IDR");
    }
    const currency = bookedByCountry
      ? getCurrencyByCountryCode(bookedByCountry)
      : (AccountController.accountData?.accountCurrency ?? "IDR");
    return formatPrice(Number(value), "", currency);
  }

  formatCodFee(value: any, bookedByCountry?: string) {
    if (this.detailData.sttDestinationCountryCode && this.detailData.sttCod.toLowerCase() === "yes") {
      const hasFraction = (num: number) => {
        return typeof num === 'number' && !Number.isInteger(num);
      }
      return `${this.formAccountCurrencyPrefix}${formatByCountry(value, {
        type: "money",
        country: this.detailData.sttDestinationCountryCode,
        fractionDigits: hasFraction(value) ? 2 : 0,
        minFractionDigits: hasFraction(value) ? 2 : 0
      })}`
    }

    const currency = bookedByCountry
      ? getCurrencyByCountryCode(bookedByCountry)
      : (AccountController.accountData?.accountCurrency ?? "IDR");
    return formatPrice(Number(value), "", currency);
  }

  get accountType() {
    return AccountController.accountData.account_type;
  }

  estimationBound(shipmentId: string, clientId: any, isCod: boolean) {
    // shipment AR
    if (this.detailData.sttReverseJourneyShipmentId.match(/^ARA|^ARB/)) {
      return false;
    }

    // shipment type by pos/internal
    if (
      shipmentId &&
      !prefixAlgoEstimationShow(shipmentId) &&
      !isShipmentC1Cod(shipmentId, isCod) &&
      (this.accountType === "partner" || this.accountType === "internal")
    ) {
      return true;
    }

    // manual type by pos
    if (!shipmentId && clientId === 0 && this.accountType === "partner") {
      return false;
    }

    // manual type by client
    if (!shipmentId && clientId === 0 && this.accountType === "client") {
      return true;
    }

    // client type by pos
    if (
      !shipmentId &&
      clientId !== 0 &&
      this.accountType === "partner" &&
      !isShipmentC1Cod(shipmentId, isCod)
    ) {
      return true;
    }

    // client type by internal
    if (
      !shipmentId &&
      clientId !== 0 &&
      this.accountType === "internal" &&
      !isShipmentC1Cod(shipmentId, isCod)
    ) {
      return true;
    }

    return (
      !shipmentId &&
      clientId !== 0 &&
      this.accountType === "client" &&
      !isShipmentC1Cod(shipmentId, isCod)
    );
  }

  formatPrice(value: any) {
    return formatPrice(
      Number(
        value
          ?.toString()
          .split(".")
          .join("")
      ),
      "",
      this.accountCurrency
    );
  }

  get shipmentId() {
    return this.detailData.isReverseJourney
      ? this.detailData.sttReverseJourneyShipmentId
      : this.detailData.sttShipmentId;
  }

  get isPriceOfCOD() {
    return this.detailData.isReverseJourney
      ? this.detailData.sttReverseJourneyCodHandling
      : this.detailData.sttIsCod;
  }

  startCase(value: string) {
    return startCase(value);
  }

  formatDateWithoutTime(value: string) {
    return formatDateWithoutTime(value);
  }

  refs: any = {};
  onOpenTooltipPromo(event: any, index: number) {
    const refs: any = this.$refs;
    refs[`infoPromo${index}`].toggle(event);
  }

  get accountIsNonForeign() {
    return !AccountController.accountData.accountIsForeign;
  }

  get additionalRate(): string {
    return this.formatCurrency(
      this.detailData.sttCommoditySurchargeRate +
        this.detailData.sttHeavyWeightSurchargeRate +
        this.detailData.sttDocumentSurchargeRate +
        this.detailData.sttWoodpackingRate,
      this.detailData.sttBookedByCountry
    );
  }

  get insuranceLabel() {
    return (this.detailData.sttBookedForType?.toLowerCase() === "pos" &&
      this.accountIsNonForeign) ||
      prefixAlgoEstimationShow(this.detailData.sttShipmentId)
      ? `${this.$t(
          "bookingShipment.estimation.shippingProtectionFee"
        )} + ${this.$t("bookingShipment.estimation.adminCost")}`
      : this.$t("bookingShipment.estimation.shippingProtectionFee");
  }
}
